import {
  applyRemoveWhiteBackgroundToCanvas,
  removeBlackFromImageData,
} from "@/home/remove-white-bg";

export type PixelRect = {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
};

export type SheetSliceGrid = {
  cols: number;
  rows: number;
};

export function clampSliceCount(n: number, max = 16): number {
  return Math.min(max, Math.max(1, Math.floor(n)));
}

export function cellPixelRect(
  imageWidth: number,
  imageHeight: number,
  grid: SheetSliceGrid,
  col: number,
  row: number,
): PixelRect {
  const cellW = imageWidth / grid.cols;
  const cellH = imageHeight / grid.rows;
  return {
    sx: Math.round(col * cellW),
    sy: Math.round(row * cellH),
    sw: Math.max(1, Math.round(cellW)),
    sh: Math.max(1, Math.round(cellH)),
  };
}

export function mergeCellRect(
  imageWidth: number,
  imageHeight: number,
  grid: SheetSliceGrid,
  col0: number,
  row0: number,
  col1: number,
  row1: number,
): PixelRect {
  const minCol = Math.min(col0, col1);
  const maxCol = Math.max(col0, col1);
  const minRow = Math.min(row0, row1);
  const maxRow = Math.max(row0, row1);
  const cellW = imageWidth / grid.cols;
  const cellH = imageHeight / grid.rows;
  return {
    sx: Math.round(minCol * cellW),
    sy: Math.round(minRow * cellH),
    sw: Math.max(1, Math.round((maxCol - minCol + 1) * cellW)),
    sh: Math.max(1, Math.round((maxRow - minRow + 1) * cellH)),
  };
}

export type CropImageOptions = {
  /** Scale longest edge down for UI previews (full crop when omitted). */
  maxEdge?: number;
  /** Make near-white pixels transparent on export. */
  removeWhiteBg?: boolean;
  /** Make near-black pixels transparent on export. */
  removeBlackBg?: boolean;
};

export function cropImageToDataUrl(
  image: HTMLImageElement,
  rect: PixelRect,
  options?: CropImageOptions,
): string {
  let outW = rect.sw;
  let outH = rect.sh;
  const maxEdge = options?.maxEdge;
  if (maxEdge && maxEdge > 0) {
    const scale = Math.min(1, maxEdge / Math.max(rect.sw, rect.sh));
    outW = Math.max(1, Math.round(rect.sw * scale));
    outH = Math.max(1, Math.round(rect.sh * scale));
  }

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("crop ภาพไม่สำเร็จ");
  }
  ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, outW, outH);
  if (options?.removeWhiteBg) {
    applyRemoveWhiteBackgroundToCanvas(ctx, outW, outH);
  }
  if (options?.removeBlackBg) {
    const imageData = ctx.getImageData(0, 0, outW, outH);
    removeBlackFromImageData(imageData);
    ctx.putImageData(imageData, 0, 0);
  }
  return canvas.toDataURL("image/png");
}

export function listAllSliceCells(
  grid: SheetSliceGrid,
): Array<{ col: number; row: number }> {
  const cells: Array<{ col: number; row: number }> = [];
  for (let row = 0; row < grid.rows; row += 1) {
    for (let col = 0; col < grid.cols; col += 1) {
      cells.push({ col, row });
    }
  }
  return cells;
}

export function selectionGridSize(
  col0: number,
  row0: number,
  col1: number,
  row1: number,
): { cellsW: number; cellsH: number } {
  return {
    cellsW: Math.abs(col1 - col0) + 1,
    cellsH: Math.abs(row1 - row0) + 1,
  };
}

export type SheetCropSelection = {
  col: number;
  row: number;
  cellsW: number;
  cellsH: number;
};

export function clampSelectionOrigin(
  grid: SheetSliceGrid,
  col: number,
  row: number,
  cellsW: number,
  cellsH: number,
): { col: number; row: number } | null {
  if (cellsW < 1 || cellsH < 1 || cellsW > grid.cols || cellsH > grid.rows) {
    return null;
  }
  return {
    col: Math.max(0, Math.min(col, grid.cols - cellsW)),
    row: Math.max(0, Math.min(row, grid.rows - cellsH)),
  };
}

export function cellInSelection(
  col: number,
  row: number,
  selection: SheetCropSelection,
): boolean {
  return (
    col >= selection.col &&
    col < selection.col + selection.cellsW &&
    row >= selection.row &&
    row < selection.row + selection.cellsH
  );
}

export function selectionToCorners(selection: SheetCropSelection): {
  col0: number;
  row0: number;
  col1: number;
  row1: number;
} {
  return {
    col0: selection.col,
    row0: selection.row,
    col1: selection.col + selection.cellsW - 1,
    row1: selection.row + selection.cellsH - 1,
  };
}
